<template>
  <div>
    <b-table
      style="border: 1px solid rgb(0, 0, 0, 0.1); border-radius: 5px; margin-top: 12px"
      :items="vorgangData"
      :fields="vorgangFields"
      :busy="isVorgangLoading"
      responsive
      hover
      head-variant="light"
      @row-clicked="rowClicked"
    >
      <template v-slot:cell(zeitraum)="row">
        <div style="width: 150px">
          {{ formatDateWithoutHours(row.item.reisedatum) }} -
          {{ formatDateWithoutHours(row.item.enddatum) }}
        </div>
      </template>
      <template v-slot:cell(kundenname)="row">
        <div style="width: 170px">
          {{ kundenName(row.item) }}
        </div>
      </template>
      <template v-slot:cell(blankState)="row">
        <VorgangStateBadge :state="row.item.blankState" />
      </template>
      <template v-slot:cell(released)="row">
        <CustomIconChecked :value="row.item.isReleased" />
      </template>
      <template v-slot:cell(fehlendePassdaten)="row">
        <AnforderungenPopover
          v-if="passdatenAnforderungen[row.item.id]"
          titel="Passdaten"
          :gruppen="passdatenAnforderungen[row.item.id].gruppen"
        >
          <TriangleAlert
            v-if="passdatenAnforderungen[row.item.id].fehlend > 0"
            :size="20"
            class="text-danger"
          />
          <CircleCheck v-else :size="20" class="text-success" />
        </AnforderungenPopover>
        <!-- Ohne Pflichtangaben oder ohne ladbare Teilnehmer wird "-" angezeigt -->
        <span v-else-if="!isVorgangLoading" class="text-muted">–</span>
      </template>
      <template v-slot:cell(aufgaben)="row">
        <b-badge :id="'tooltip-target-vorgang' + row.item.id">
          {{ row.item.aufgaben?.length }}
        </b-badge>
        <b-tooltip
          :target="'tooltip-target-vorgang' + row.item.id"
          triggers="hover"
          custom-class="custom-tooltip"
          v-if="row.item.aufgaben?.length > 1"
        >
          <AufgabenWidget
            :openAufgabenId="openAufgabenId"
            class="mt-3"
            :ansicht="'aufgabe-details'"
            :aufgaben="row.item.aufgaben || []"
          ></AufgabenWidget>
        </b-tooltip>
      </template>
    </b-table>
  </div>
</template>
<script>
import VorgangStateBadge from '@/components/vorgaenge/vorgang-state-badge.vue';
import AufgabenWidget from '@/components/produkte/reisetermine/aufgaben-widget.vue';
import apiService from '@/core/common/services/api.service';
import CustomIconChecked from '@/components/common/custom-icon-checked.vue';
import AnforderungenPopover from '@/components/common/anforderungen-popover.vue';
import { formatDateWithoutHours } from '@/core/common/helpers/utils';
import { buildAnforderungsAbschnitte } from '@/core/vorgaenge/helpers/passdaten-anforderungen';
import { CircleCheck, TriangleAlert } from 'lucide-vue';

export default {
  components: {
    VorgangStateBadge,
    AufgabenWidget,
    CustomIconChecked,
    AnforderungenPopover,
    CircleCheck,
    TriangleAlert,
  },
  props: {
    vorgangData: { type: Array, required: true },
    openAufgabenId: { type: Number, default: null },
  },
  data() {
    return {
      isVorgangLoading: false,
      vorgangDataExpanded: [],
      teilnehmerPassdatenRequirements: null,
      vorgangFields: [
        {
          tdClass: 'align-middle',
          key: 'id',
          label: 'BNR',
        },
        {
          tdClass: 'align-middle',
          key: 'pnr',
          label: 'PNR',
          formatter: value => value?.join(','),
        },
        {
          tdClass: 'align-middle',
          key: 'kundenname',
        },
        {
          tdClass: 'align-middle',
          key: 'zimmer',
          formatter: value => value.join(', '),
        },
        {
          tdClass: 'align-middle',
          key: 'npx',
          label: 'Pax',
        },
        {
          tdClass: 'align-middle',
          key: 'blankState',
          label: 'Buchungsstatus',
        },
        {
          tdClass: 'align-middle',
          key: 'released',
        },
        { tdClass: 'align-middle', key: 'fehlendePassdaten', label: 'Passdaten vorhanden' },
        { tdClass: 'align-middle', key: 'aufgaben', label: 'Aufgaben' },
      ],
    };
  },
  mounted() {
    this.loadAllTeilnehmer();
  },
  watch: {
    vorgangData: 'loadAllTeilnehmer',
  },
  computed: {
    // Je Vorgang die Popover-Gruppen und die Gesamtzahl fehlender Pflichtangaben
    passdatenAnforderungen() {
      if (this.teilnehmerPassdatenRequirements == null) return {};

      return Object.entries(this.teilnehmerPassdatenRequirements).reduce((acc, [bnr, teilnehmerListe]) => {
        const gruppen = teilnehmerListe
          .map(teilnehmer => ({
            titel: teilnehmer.kunde,
            abschnitte: buildAnforderungsAbschnitte(teilnehmer.anforderungen),
          }))
          .filter(gruppe => gruppe.abschnitte.length > 0);

        if (gruppen.length === 0) return acc;

        acc[bnr] = {
          gruppen,
          fehlend: gruppen
            .flatMap(gruppe => gruppe.abschnitte)
            .flatMap(abschnitt => abschnitt.eintraege)
            .filter(eintrag => !eintrag.erfuellt).length,
        };
        return acc;
      }, {});
    },
  },
  methods: {
    formatDateWithoutHours,
    kundenName(item) {
      if (item?.kunde?.master) {
        return item?.kunde?.master.vorname + ' ' + item?.kunde?.master.nachname;
      } else return '';
    },
    rowClicked(item) {
      //   window.open('/#/reisen/' + item.reisekuerzel + '/edit', '_blank');
    },
    // Lädt die Teilnehmer aller Vorgänge der Tabelle und legt sie nach Buchungsnummer gruppiert ab
    async loadAllTeilnehmer() {
      this.isVorgangLoading = true;
      try {
        const result = await Promise.all(
          this.vorgangData.map(vorgang => {
            const reiseterminId = vorgang.hotelleistungen?.[0]?.reiseterminId;
            if (reiseterminId == null) return [];

            return this.getVorgangTeilnehmerPassdatenRequirementsById(vorgang.id, reiseterminId).catch(
              () => []
            );
          })
        );
        this.teilnehmerPassdatenRequirements = result.flat().reduce((acc, curr) => {
          const teilnehmer = {
            kunde: curr.name,
            anforderungen: Object.entries(curr)
              .filter(([, value]) => value?.required === true)
              .map(([key, value]) => ({ key, erfuellt: value.provided === true })),
          };
          acc[curr.bnr] = [...(acc[curr.bnr] || []), teilnehmer];
          return acc;
        }, {});
      } catch {
        this.teilnehmerPassdatenRequirements = {};
      } finally {
        this.isVorgangLoading = false;
      }
    },
    getVorgangTeilnehmerPassdatenRequirementsById(vorgangId, reiseterminId) {
      return apiService.get(`Hotelleistung/${vorgangId}/${reiseterminId}/teilnehmer`).then(({ data }) => {
        return (
          (data.result &&
            data.result.teilnehmer &&
            data.result.teilnehmer.filter(teilnehmer => !teilnehmer.isDeleted)) ||
          []
        );
      });
    },
  },
};
</script>
<style>
.custom-tooltip .tooltip-inner {
  max-height: 500px;
  overflow-y: auto;
  text-align: left;
}
</style>
